import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/** Body for "SIEM vs log management vs XDR" comparison guide. */
export function SiemVsLogManagementVsXdrGuideBody() {
  return (
    <>
      <Lead>
        SIEM, log management and XDR overlap enough to be confusing, and vendors do
        not help. All three touch logs, but they exist for different jobs: storing
        and searching logs, detecting and investigating threats, and responding
        across endpoints and network. This guide draws the lines so you can tell what
        you actually need.
      </Lead>

      <GuideSection id="definitions" title="The three, defined">
        <UL>
          <LI><Term>Log management</Term>: collect, store, search and retain logs from across your estate. The foundation; used by ops, dev and security alike. See <GuideLink href="/guides/what-is-log-management">what is log management</GuideLink>.</LI>
          <LI><Term>SIEM</Term>, Security Information and Event Management: correlate and analyse log data to detect, investigate and respond to security threats, with long retention for compliance. See <GuideLink href="/guides/what-is-siem">what is SIEM</GuideLink>.</LI>
          <LI><Term>XDR</Term>, Extended Detection and Response: a threat-centric platform that unifies endpoint, network and cloud telemetry for fast, often automated detection and response.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="differences" title="Key differences">
        <GuideTable
          head={['', 'Log management', 'SIEM', 'XDR']}
          rows={[
            ['Primary goal', 'Store & search logs', 'Detect, investigate, comply', 'Detect & respond fast'],
            ['Data scope', 'All logs', 'Logs + security telemetry', 'Endpoint, network, cloud telemetry'],
            ['Retention', 'Flexible / long', 'Long (compliance, forensics)', 'Shorter (30–90 days typical)'],
            ['Response', 'None (storage)', 'Workflow + SOAR', 'Built-in, often automated'],
            ['Best for', 'Everyone', 'Compliance + broad visibility', 'Agile threat response'],
          ]}
        />
      </GuideSection>

      <GuideSection id="retention" title="Retention and forensics: a key split">
        <P>
          XDR tends to keep high-volume telemetry for a shorter window (often 30–90
          days), which makes it less suited to long-term compliance or investigating
          attacks that started months ago. SIEM and log management keep data longer
          for forensic and regulatory needs, important when frameworks like{' '}
          <GuideLink href="/guides/nis2-logging-requirements">NIS2</GuideLink> and{' '}
          <GuideLink href="/guides/dora-logging-monitoring-requirements">DORA</GuideLink>{' '}
          drive your retention. See{' '}
          <GuideLink href="/guides/log-retention-requirements">log retention requirements</GuideLink>.
        </P>
      </GuideSection>

      <GuideSection id="when-to-use" title="Which do you need?">
        <UL>
          <LI><Term>Choose log management</Term> when you primarily need to centralise, search and retain logs for troubleshooting and basic monitoring.</LI>
          <LI><Term>Choose SIEM</Term> when you need broad visibility, threat detection, long retention, and compliance evidence across your whole IT estate.</LI>
          <LI><Term>Choose XDR</Term> when you want integrated, fast, often-automated detection and response across endpoints, network and cloud with a small team.</LI>
        </UL>
        <Callout title="They are not mutually exclusive">
          <p>
            Most organisations need log management as the base and add SIEM
            capability for security. The categories increasingly converge onto one
            engine rather than three separate products and bills.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse fits">
        <P>
          LogPulse spans <Term>log management and SIEM on one engine</Term>: the same
          LPQL + ClickHouse store powers search, service intelligence, and a
          risk-based SIEM, with retention long enough for compliance. So you get the
          searchable base and the security layer without feeding and paying for
          separate stacks. See{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> and the{' '}
          <GuideLink href="/compare">comparison overview</GuideLink>.
        </P>
      </GuideSection>
    </>
  );
}
